import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { PagerPosition, PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import Pager from '@aurora/shared-client/components/common/Pager/Pager';
import type { PagerLoadMoreTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import PaginationHelper from '@aurora/shared-client/helpers/ui/PaginationHelper/PaginationHelper';
import type { SearchResultsPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import React, { useContext, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import type {
  ContextNodeFragment,
  ContextUserFragment,
  TagListForMessageSearchFilterQuery,
  TagListForMessageSearchFilterQueryVariables
} from '../../../../types/graphql-types';
import useTranslation from '../../../useTranslation';
import { getSearchFilterParams } from '../../SearchHelper';
import localStyles from '../SearchWidgetFilters/SearchWidgetFilters.module.pcss';
import tagListForMessageSearchFilter from '../SearchWidgetFilters/TagListForMessageSearchFilter.query.graphql';

interface Props {
  /**
   * The name reference to the filter
   */
  filterName: string;
  /**
   * Number of results to be rendered
   */
  pageSize?: number;
  /**
   * The pagination type used
   */
  pagerVariant?: PagerLoadMoreTypeAndProps;
  /**
   * The author(user) data, selected from author filter
   */
  author?: ContextUserFragment;
  /**
   * Node which search should be scoped to. When not provided, search is scoped to community.
   */
  contextNode?: ContextNodeFragment;
}

/**
 * Render labels filter dropdown with tags options
 *
 * @author Arica Chakraborty
 */
const MessageSearchTagsFilter: React.FC<React.PropsWithChildren<Props>> = ({
  filterName,
  pageSize = 5,
  pagerVariant = {
    type: PagerVariant.LOAD_MORE,
    props: {
      position: PagerPosition.START,
      clearLoadingAfterUpdate: true
    }
  },
  author,
  contextNode
}) => {
  const cx = useClassNameMapper(localStyles);
  const { authUser } = useContext(AppContext);
  const { router } = useEndUserRoutes();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const queryTerm = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_KEY);
  const tagTypeFilter = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_BY_TAGS_FILTER, '');
  const i18n = useTranslation(EndUserComponent.MESSAGE_SEARCH_TAGS_FILTER);
  const { formatMessage, loading: textLoading } = i18n;

  const tagsVariables: TagListForMessageSearchFilterQueryVariables = {
    searchTerm: queryTerm,
    constraints: {
      ...getSearchFilterParams(router.query, authUser, author, contextNode, false)
    },
    first: pageSize,
    useFullPageInfo: true
  };

  const queryResult = useQueryWithTracing<
    TagListForMessageSearchFilterQuery,
    TagListForMessageSearchFilterQueryVariables
  >(module, tagListForMessageSearchFilter, {
    variables: tagsVariables,
    fetchPolicy: 'network-only'
  });

  const paginationHelper = PaginationHelper.fromQueryResult<
    TagListForMessageSearchFilterQuery,
    TagListForMessageSearchFilterQueryVariables
  >(queryResult, pageSize, null, null);

  if (textLoading) {
    return null;
  }

  /**
   * Handle the selected filter item and update the query parameter
   *
   * @param item The selected filter item
   */
  async function handleFilterMenuItemClick(item: string): Promise<void> {
    await router.replaceRoute<SearchResultsPageAndParams>(
      EndUserPages.SearchResultsPage,
      null,
      {
        ...router.query,
        [EndUserQueryParams.SEARCH_BY_TAGS_FILTER]: item
      },
      { shallow: true }
    );
  }

  return (
    <Dropdown
      onToggle={isOpen => {
        setDropdownOpen(isOpen);
      }}
      data-menu-open={dropdownOpen}
    >
      <Dropdown.Toggle
        as={Button}
        variant={ButtonVariant.LINK}
        size="lg"
        className={cx('lia-g-dropdown-toggle')}
        data-testid={`SearchPage.Dropdown.${filterName}`}
        disabled={queryResult.data?.tagListForMessageSearchFilter.totalCount == 0}
        aria-label={formatMessage('tagsFilterLabel')}
      >
        {filterName}
      </Dropdown.Toggle>
      <Dropdown.Menu popperConfig={dropdownPopperConfig}>
        {dropdownOpen &&
          !queryResult.loading &&
          queryResult.data?.tagListForMessageSearchFilter.edges.map(({ node }) => (
            <Dropdown.Item
              key={node.id}
              active={node.text === tagTypeFilter}
              data-testid={`SearchPage.Dropdown.${filterName}.Item`}
              onSelect={(): Promise<void> => handleFilterMenuItemClick(node.text)}
            >
              <span className={cx('lia-menu-item-content')}>{node.text.toUpperCase()}</span>
              <span className={cx('lia-menu-item-count')}> {node.messagesCount}</span>
            </Dropdown.Item>
          ))}
        <Pager
          className={cx('lia-g-px-20 lia-g-py-5')}
          loadPage={paginationHelper.loadPage}
          pageInfo={paginationHelper.pageInfo}
          variant={pagerVariant}
        />
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MessageSearchTagsFilter;
